/**
 * Arpeggiator — a note-router that sits between keyboards/MIDI and the
 * VoiceManager. When enabled, incoming note-ons are captured in a held
 * set; a Transport-synced clock walks through that set in the configured
 * pattern and fires noteOn/noteOff on downstream voices. When disabled,
 * notes pass straight through to the passThrough handlers.
 *
 * Pattern modes:
 *   - up:      sorted ascending by pitch, repeat
 *   - down:    sorted descending by pitch, repeat
 *   - updown:  up then down (ping-pong), not re-striking the turnaround notes
 *   - order:   in the order keys were pressed (newest press moves to the end)
 *   - random:  pick a random held note each step
 *
 * Timing is a plain setTimeout scheduler — not sample-accurate, but arp
 * rates are far below audio rate so drift is inaudible at any musical BPM.
 * Gate length is a fraction of the step duration: short = staccato,
 * 1.0 = legato (each note rings until the next step).
 */

import { EnumParameter, NumericParameter, type AnyParameter } from '../params/Parameter';
import type { AudioEngine } from './AudioEngine';
import type { ModuleState, SynthModule } from './modules/SynthModule';
import { mintId, readParamState, writeParamState } from './modules/util';
import type { Transport, SyncMode } from './Transport';

export type ArpMode = 'up' | 'down' | 'updown' | 'order' | 'random';
export const ARP_MODES: readonly ArpMode[] = ['up', 'down', 'updown', 'order', 'random'];

/** Divisions only — 'free' doesn't make sense here; arps are rhythmic. */
const ARP_RATES: readonly SyncMode[] = [
  '1/1',
  '1/2',
  '1/4',
  '1/4T',
  '1/8',
  '1/8T',
  '1/16',
  '1/16T',
];

export type ArpStepHandler = (note: number, velocity: number, when?: number) => void;
export type ArpReleaseHandler = (note: number) => void;

export interface ArpPassthrough {
  readonly onNoteOn: ArpStepHandler;
  readonly onNoteOff: ArpReleaseHandler;
}

interface HeldNote {
  readonly note: number;
  readonly velocity: number;
  readonly order: number;
}

export class ArpEngine implements SynthModule {
  readonly kind = 'modulation' as const;
  readonly type = 'arp' as const;
  readonly displayName = 'ARP';
  readonly id: string;
  bypass = false;

  readonly input: AudioNode | null = null;
  readonly output: AudioNode | null = null;

  readonly enabled = new EnumParameter<'off' | 'on'>({
    name: 'Enabled',
    default: 'off',
    options: ['off', 'on'],
    randomizable: false,
  });

  readonly mode = new EnumParameter<ArpMode>({
    name: 'Mode',
    default: 'up',
    options: ARP_MODES,
    formatter: (v) => v.toUpperCase(),
  });

  readonly rate = new EnumParameter<SyncMode>({
    name: 'Rate',
    default: '1/8',
    options: ARP_RATES,
    randomizable: false,
  });

  // Named `gateLength` internally to avoid colliding with SynthModule's
  // optional `gate(open, at)` method. The user-visible label is still "Gate".
  readonly gateLength = new NumericParameter({
    name: 'Gate',
    default: 0.5,
    min: 0.05,
    max: 1,
    curve: 'linear',
    formatter: (v) => `${Math.round(v * 100)}%`,
    randomizable: false,
  });

  readonly octaves = new NumericParameter({
    name: 'Octaves',
    default: 1,
    min: 1,
    max: 4,
    curve: 'linear',
    formatter: (v) => `${Math.round(v)}`,
    smoothingMs: 0,
    randomizable: false,
  });

  readonly parameters: readonly AnyParameter[] = [
    this.enabled,
    this.mode,
    this.rate,
    this.gateLength,
    this.octaves,
  ];

  private readonly transport: Transport;
  private passthrough: ArpPassthrough | null = null;
  private stepHandler: ArpStepHandler | null = null;
  private releaseHandler: ArpReleaseHandler | null = null;

  /** Held notes in press order. Sorting for up/down happens at pick time. */
  private held: HeldNote[] = [];
  private orderCounter = 0;
  private currentNote: number | null = null;
  private stepIndex = 0;
  private stepTimer: ReturnType<typeof setTimeout> | null = null;
  private gateTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(transport: Transport, id?: string) {
    this.id = id ?? mintId('arp');
    this.transport = transport;

    // Toggling enabled mid-play should cleanly stop any pending step/gate
    // so a disable doesn't leave a hanging note.
    this.enabled.subscribe((v) => {
      if (v === 'off') {
        this.panic();
      }
    });
  }

  init(_engine: AudioEngine): void {
    // No audio to wire — the arp is a note-level module.
  }

  dispose(): void {
    this.panic();
  }

  /**
   * Register the downstream handlers: `onStep`/`onRelease` drive the
   * arp pattern, and `passthrough` routes notes when the arp is off.
   */
  setHandlers(opts: {
    onStep: ArpStepHandler;
    onRelease: ArpReleaseHandler;
    passthrough: ArpPassthrough;
  }): void {
    this.stepHandler = opts.onStep;
    this.releaseHandler = opts.onRelease;
    this.passthrough = opts.passthrough;
  }

  /** Note-on from upstream (MIDI, keyboard). Routed to arp or passthrough. */
  noteOn(note: number, velocity: number, when?: number): void {
    if (this.enabled.value === 'off') {
      this.passthrough?.onNoteOn(note, velocity, when);
      return;
    }
    // If the note is already held (re-press), move it to the end for
    // `order` mode — mirrors how a real keyboard feels.
    const existing = this.held.findIndex((h) => h.note === note);
    if (existing >= 0) this.held.splice(existing, 1);
    this.held.push({ note, velocity, order: this.orderCounter++ });
    if (!this.stepTimer) this.startClock();
  }

  /** Note-off from upstream. */
  noteOff(note: number): void {
    if (this.enabled.value === 'off') {
      this.passthrough?.onNoteOff(note);
      return;
    }
    const idx = this.held.findIndex((h) => h.note === note);
    if (idx >= 0) this.held.splice(idx, 1);
    if (this.held.length === 0) this.stopClock();
  }

  /** Hard reset: clear held, release current step, stop the clock. */
  panic(): void {
    this.stopClock();
    this.held.length = 0;
  }

  // ── Module serialization ──────────────────────────────────────────────

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    readParamState(this.parameters, state.params, 'preset');
  }

  /**
   * Reset all parameters to their defaults and stop any running pattern.
   * Called by PresetManager when a loaded preset doesn't include arp
   * state — the arp shouldn't linger from the previous patch.
   */
  resetToDefaults(): void {
    this.panic();
    for (const p of this.parameters) p.reset('preset');
  }

  // ── Internals ──────────────────────────────────────────────────────────

  private startClock(): void {
    // Reset traversal state so an arp re-engaging from empty starts fresh.
    // Don't fire an immediate tick here — the user typically presses a
    // chord note-by-note and wants the whole chord to be captured before
    // the pattern starts. Firing the first step instantly would play the
    // first-pressed note alone, then cycle through the full chord later.
    this.stepIndex = 0;
    this.scheduleNextStep();
  }

  private stopClock(): void {
    if (this.stepTimer) {
      clearTimeout(this.stepTimer);
      this.stepTimer = null;
    }
    if (this.gateTimer) {
      clearTimeout(this.gateTimer);
      this.gateTimer = null;
    }
    if (this.currentNote !== null) {
      this.releaseHandler?.(this.currentNote);
      this.currentNote = null;
    }
  }

  private scheduleNextStep(): void {
    const stepMs = this.stepMs();
    this.stepTimer = setTimeout(() => {
      this.tick();
      if (this.held.length > 0) this.scheduleNextStep();
    }, stepMs);
  }

  /** Current step duration in milliseconds, derived from BPM + rate. */
  private stepMs(): number {
    const rate = this.rate.value;
    if (rate === 'free') return 125;
    const stepSec = this.transport.divisionToSeconds(rate);
    return Math.max(1, stepSec * 1000);
  }

  private tick(): void {
    const note = this.pickNote();
    if (note === null) return;

    // Release the previous step's note (if still ringing) before firing
    // the new one. With short gate values the gateTimer has already
    // released; this is the safety net for legato gate=1.
    if (this.currentNote !== null && this.currentNote !== note) {
      this.releaseHandler?.(this.currentNote);
    }

    const velocity = this.velocityFor(note);
    this.stepHandler?.(note, velocity);
    this.currentNote = note;

    // Schedule the gate release. Gate is a fraction of the step duration.
    const gateMs = Math.max(5, this.stepMs() * this.gateLength.value);
    if (this.gateTimer) clearTimeout(this.gateTimer);
    this.gateTimer = setTimeout(() => {
      if (this.currentNote === note) {
        this.releaseHandler?.(note);
        this.currentNote = null;
      }
    }, gateMs);
  }

  /** Map a pattern-emitted note (possibly octave-shifted) back to its
   *  held-note velocity — shifted notes inherit from the base held note. */
  private velocityFor(note: number): number {
    // Strip octave shift: try the note itself first, then octave-shifted
    // matches (the pattern builder doesn't mutate velocity, so we just
    // look up by modulo-12 similarity).
    for (const h of this.held) {
      if (h.note === note) return h.velocity;
    }
    for (const h of this.held) {
      if ((h.note - note) % 12 === 0) return h.velocity;
    }
    return 0.8;
  }

  private pickNote(): number | null {
    const pattern = this.patternNotes();
    if (pattern.length === 0) return null;
    if (this.mode.value === 'random') {
      return pattern[Math.floor(Math.random() * pattern.length)] ?? null;
    }
    // Mod at access only, not at increment. That way if the held chord
    // grows mid-pattern (user adds a note), the advancing stepIndex keeps
    // visiting fresh positions instead of looping early.
    const picked = pattern[this.stepIndex % pattern.length] ?? null;
    this.stepIndex++;
    return picked;
  }

  /**
   * Build the note sequence for this pattern step. `order` keeps press
   * order; up/down sort by pitch; updown concatenates up+reversed(up)
   * minus the endpoints (so turnarounds aren't repeated).
   *
   * Octave stacking: the base sequence is repeated `octaves` times with
   * +12 semitone shift per pass, matching every synth arp ever built.
   */
  private patternNotes(): number[] {
    const base = this.baseSequence();
    const octaves = Math.max(1, Math.round(this.octaves.value));
    if (octaves === 1) return base;
    const out: number[] = [];
    for (let o = 0; o < octaves; o++) {
      for (const n of base) out.push(n + o * 12);
    }
    return out;
  }

  private baseSequence(): number[] {
    switch (this.mode.value) {
      case 'up':
        return this.held.map((h) => h.note).sort((a, b) => a - b);
      case 'down':
        return this.held.map((h) => h.note).sort((a, b) => b - a);
      case 'order':
        return [...this.held]
          .sort((a, b) => a.order - b.order)
          .map((h) => h.note);
      case 'updown': {
        const up = this.held.map((h) => h.note).sort((a, b) => a - b);
        if (up.length <= 1) return up;
        // Classic ping-pong: up then down, dropping the two endpoints on
        // the way back so they don't double-strike.
        return [...up, ...up.slice(1, -1).reverse()];
      }
      case 'random':
        return this.held.map((h) => h.note);
    }
  }
}
